import { useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { getSquadDetails } from '../services/squadApi';

export function useRealtimeSubscription(
  squadId,
  onSquadUpdate,
  onMessageReceived
) {
  const realtimeChannelRef = useRef(null);
  const callbacksRef = useRef({ onSquadUpdate, onMessageReceived });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onSquadUpdate, onMessageReceived };
  }, [onSquadUpdate, onMessageReceived]);

  useEffect(() => {
    if (!squadId || !supabase) return;

    // Clean up existing channel if squadId changes
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    // Create channel for this squad
    const channel = supabase
      .channel(`squad:${squadId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'squads',
          filter: `id=eq.${squadId}`,
        },
        async () => {
          // Squad data changed - refresh squad details
          try {
            const data = await getSquadDetails(squadId);
            if (data) {
              callbacksRef.current.onSquadUpdate(data);
            }
          } catch (error) {
            console.error('Error refreshing squad details:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'squad_members',
          filter: `squad_id=eq.${squadId}`,
        },
        async () => {
          // Squad members changed - refresh squad details to get updated member list
          try {
            const data = await getSquadDetails(squadId);
            if (data) {
              callbacksRef.current.onSquadUpdate(data);
            }
          } catch (error) {
            console.error('Error refreshing squad members:', error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'squad_messages',
          filter: `squad_id=eq.${squadId}`,
        },
        (payload) => {
          // New message received - add to messages list
          const newMsg = payload.new;
          if (newMsg) {
            callbacksRef.current.onMessageReceived(newMsg);
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    // Cleanup on unmount or squadId change
    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadId]); // getSquadDetails and supabase are stable imports
}
